/**
 * MainLayout — the shell that wraps every authenticated admin page.
 *
 * Structure:
 *   ┌─────────────────────────────────────────────┐
 *   │ Sidebar │            Header                 │
 *   │         ├───────────────────────────────────┤
 *   │         │            TabBar                 │
 *   │         ├───────────────────────────────────┤
 *   │         │                                   │
 *   │         │       <Outlet /> (page frame)     │
 *   │         │                                   │
 *   └─────────────────────────────────────────────┘
 *
 * The router renders the active page into <Outlet/>. Tab KeepAlive is
 * intentionally simple: when an operator closes a tab we rely on react-query
 * caches for data — form state is not persisted across closes, which
 * matches EasyAdmin's default behavior.
 */
import { Layout } from 'antd';
import { Outlet, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { TabBar } from './TabBar';
import { useTabsStore } from '../store/tabs';
import { ADMIN_MENU_LEAVES, DEFAULT_HOME_TAB } from '../config/admin-menu-tree';

const { Content } = Layout;

export function MainLayout() {
  const location = useLocation();
  const openTab = useTabsStore((s) => s.open);

  useEffect(() => {
    const { tabs } = useTabsStore.getState();
    if (tabs.length > 0) return;
    const path = location.pathname;
    if (!path || path === '/') {
      openTab(DEFAULT_HOME_TAB);
      return;
    }
    const leaf = ADMIN_MENU_LEAVES.find((l) => l.path === path);
    if (leaf) {
      const search = location.search ?? '';
      const id = new URLSearchParams(search).get('id');
      const detailUser = leaf.route === 'platform.user.detail' && id;
      openTab({
        key: detailUser ? `${leaf.route}:${id}` : leaf.route,
        label: detailUser ? `会员 #${id}` : leaf.labelZh,
        path: `${leaf.path}${search}`,
        closable: leaf.route !== 'platform.dashboard',
      });
    } else {
      openTab(DEFAULT_HOME_TAB);
    }
  }, [location.pathname, location.search, openTab]);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sidebar />
      <Layout>
        <Header />
        <TabBar />
        <Content style={{ padding: 0 }}>
          <div className="uo-page-frame">
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
}
